import React, { useState, useEffect, useRef } from 'react';
import useUIStore from '@/app/store/ui.store';
import { useAuth } from '@/shared/hooks/useAuth';
import { useTranslation } from '@/shared/i18n';
import { supportedLanguages, Language } from '@/shared/i18n/config';
import { cn } from '@/shared/utils/helpers/cn';
import { Icon } from '@/design-system/primitives';

export const Header: React.FC = () => {
  const { setSidebarOpen, theme, setTheme } = useUIStore();
  const { user, logout } = useAuth();
  const { t, i18n } = useTranslation();
  const [isLanguageDropdownOpen, setIsLanguageDropdownOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const languageDropdownRef = useRef<HTMLDivElement>(null);
  const userMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (languageDropdownRef.current && !languageDropdownRef.current.contains(event.target as Node)) {
        setIsLanguageDropdownOpen(false);
      }
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setIsUserMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const changeLanguage = (lng: Language) => {
    i18n.changeLanguage(lng);
  };

  const getCurrentLanguage = (): Language => {
    return (i18n.language as Language) || 'fr';
  };

  const handleThemeToggle = () => {
    const themes = ['light', 'dark', 'system'] as const;
    const currentIndex = themes.indexOf(theme);
    const nextTheme = themes[(currentIndex + 1) % themes.length];
    setTheme(nextTheme);
  };

  const getThemeIcon = () => {
    switch (theme) {
      case 'light':
        return <Icon name="Sun" size={20} />;
      case 'dark':
        return <Icon name="Moon" size={20} />;
      case 'system':
        return <Icon name="Monitor" size={20} />;
      default:
        return <Icon name="Sun" size={20} />;
    }
  };

  const displayName = user ? `${user.firstName} ${user.lastName}`.trim() || user.username : 'User';
  const initials = displayName.split(' ').map(n => n.charAt(0)).join('').slice(0, 2).toUpperCase();

  return (
    <header className="bg-white/80 dark:bg-neutral-800/80 backdrop-blur-xl border-b border-neutral-200/60 dark:border-neutral-700/60 h-16 flex items-center justify-between px-4 lg:px-6 sticky top-0 z-30">
      {/* Left Section */}
      <div className="flex items-center space-x-4">
        <button
          onClick={() => setSidebarOpen(true)}
          className="lg:hidden p-2 rounded-lg text-neutral-500 hover:text-neutral-700 dark:text-neutral-400 dark:hover:text-neutral-200 hover:bg-neutral-100 dark:hover:bg-neutral-700 transition-all duration-200"
        >
          <Icon name="Menu" size={20} />
        </button>
      </div>

      {/* Right Section */}
      <div className="flex items-center space-x-2">
        {/* Language Selector */}
        <div className="relative" ref={languageDropdownRef}>
          <button
            onClick={() => setIsLanguageDropdownOpen(!isLanguageDropdownOpen)}
            className="flex items-center space-x-1.5 px-2.5 py-1.5 text-sm font-medium text-neutral-600 dark:text-neutral-300 bg-neutral-100/80 dark:bg-neutral-700/80 rounded-lg hover:bg-neutral-200/80 dark:hover:bg-neutral-600/80 transition-all duration-200"
          >
            <Icon name="Languages" size={16} />
            <span className="hidden sm:inline text-xs font-semibold">
              {supportedLanguages.find(lang => lang.code === getCurrentLanguage())?.code.toUpperCase()}
            </span>
            <Icon name="ChevronDown" size={16} className={cn('w-3 h-3 transition-transform duration-200', isLanguageDropdownOpen && 'rotate-180')} />
          </button>

          {isLanguageDropdownOpen && (
            <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-neutral-800 rounded-xl shadow-xl py-1.5 z-50 border border-neutral-200/80 dark:border-neutral-700/80 ring-1 ring-black/5 animate-fade-in">
              {supportedLanguages.map((language) => (
                <button
                  key={language.code}
                  onClick={() => {
                    changeLanguage(language.code);
                    setIsLanguageDropdownOpen(false);
                  }}
                  className={cn(
                    'block w-full text-left px-4 py-2 text-sm transition-colors duration-150',
                    getCurrentLanguage() === language.code
                      ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300'
                      : 'text-neutral-700 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-neutral-700/50'
                  )}
                >
                  <div className="flex items-center justify-between">
                    <span>{language.nativeName}</span>
                    <span className="text-[10px] font-bold text-neutral-400 dark:text-neutral-500 uppercase">
                      {language.code}
                    </span>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Theme Toggle */}
        <button
          onClick={handleThemeToggle}
          className="p-2 rounded-lg text-neutral-500 hover:text-neutral-700 dark:text-neutral-400 dark:hover:text-neutral-200 hover:bg-neutral-100 dark:hover:bg-neutral-700 transition-all duration-200"
        >
          {getThemeIcon()}
        </button>

        {/* Notifications */}
        <button className="relative p-2 rounded-lg text-neutral-500 hover:text-neutral-700 dark:text-neutral-400 dark:hover:text-neutral-200 hover:bg-neutral-100 dark:hover:bg-neutral-700 transition-all duration-200">
          <Icon name="Bell" size={20} />
          <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-red-500 rounded-full ring-2 ring-white dark:ring-neutral-800" />
        </button>

        {/* Divider */}
        <div className="w-px h-6 bg-neutral-200 dark:bg-neutral-700 mx-1" />

        {/* User Menu */}
        <div className="relative" ref={userMenuRef}>
          <button
            onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
            className="flex items-center space-x-2.5 px-2 py-1.5 rounded-lg text-sm font-medium text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-700 transition-all duration-200"
          >
            <div className="w-8 h-8 bg-gradient-to-br from-primary-500 to-indigo-600 rounded-full flex items-center justify-center shadow-sm shadow-primary-500/25">
              <span className="text-white font-semibold text-xs">{initials}</span>
            </div>
            <div className="hidden lg:block text-left">
              <p className="text-sm font-medium text-neutral-900 dark:text-white leading-tight">
                {displayName}
              </p>
            </div>
            <Icon name="ChevronDown" size={16} className={cn('hidden lg:block w-3.5 h-3.5 text-neutral-400 transition-transform duration-200', isUserMenuOpen && 'rotate-180')} />
          </button>

          {isUserMenuOpen && (
            <div className="absolute right-0 mt-2 w-56 bg-white dark:bg-neutral-800 rounded-xl shadow-xl py-1 z-50 border border-neutral-200/80 dark:border-neutral-700/80 ring-1 ring-black/5 animate-fade-in">
              {/* User Info */}
              <div className="px-4 py-3 border-b border-neutral-100 dark:border-neutral-700/50">
                <p className="text-sm font-semibold text-neutral-900 dark:text-white">
                  {displayName}
                </p>
                <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-0.5 truncate">
                  {user?.email || ''}
                </p>
              </div>

              {/* Menu Items */}
              <div className="py-1">
                <button
                  onClick={() => setIsUserMenuOpen(false)}
                  className="flex items-center w-full px-4 py-2.5 text-sm text-neutral-700 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-neutral-700/50 transition-colors duration-150"
                >
                  <Icon name="CircleUserRound" size={16} className="mr-3 text-neutral-400" />
                  {t('header.profile')}
                </button>

                <button
                  onClick={() => setIsUserMenuOpen(false)}
                  className="flex items-center w-full px-4 py-2.5 text-sm text-neutral-700 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-neutral-700/50 transition-colors duration-150"
                >
                  <Icon name="Settings" size={16} className="mr-3 text-neutral-400" />
                  {t('header.settings')}
                </button>

                <div className="my-1 border-t border-neutral-100 dark:border-neutral-700/50" />

                <button
                  onClick={() => {
                    setIsUserMenuOpen(false);
                    logout();
                  }}
                  className="flex items-center w-full px-4 py-2.5 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors duration-150"
                >
                  <Icon name="LogOut" size={16} className="mr-3" />
                  {t('header.logout')}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};
